/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-11-04 11:28:18
 */
import React, { useState, useEffect, useCallback } from 'react'
import { Button, Select } from 'antd'
import { UnorderedListOutlined } from '@ant-design/icons'
import sensors, { sensorsRole, sensorsVipLevel } from '../../../utils/sensors'
import { useAppSelector } from '../../../app/hooks'
import baseColumnsData from '../lib/baseColumnsData'
import { AI_EXTRACT_SCENARIOS } from '../lib/lib'
import aiIcon from '../../../images/photo-watermark/ai_vector.png'
import aiGuideImage from '../../../images/photo-watermark/ai_guide.png'
import AiExtractModal from './AiExtractModal'

const { Option } = Select

interface IColumnsSettingProps {
  checkedBaseColumns: string[]
  extraColumns: string[]
  customerLableColumns: string[]
  checkedExtraColumns: string[]
  checkedCustomerLableColumns: string[]
  checkedAiColumns?: string[]
  onChange: (
    baseColumns: string[],
    extraColumns: string[],
    customerLableColumns: string[],
    checkedAiColumns?: string[]
  ) => void
  handleSensorClickItem: (clickItem: string, params?: any) => void
  isOpenNeteaseShelfReview?: boolean
  aiExtractConfig?: {
    isEnabled: boolean
    selectedScenarios: string[]
    extractedPhotos: Set<string>
    isExtracting: boolean
    isFilterEnabled?: boolean
  }
  onAiExtractSave?: (selectedOptions: string[]) => void
  onCloseAiExtract?: () => void
  onRetryExtraction?: () => void
  onStartAiExtraction?: (forceRefresh?: boolean) => void
  onCancelAiExtraction?: () => void
  onToggleAiFilter?: (checked: boolean) => void
}

const ColumnsSetting: React.FC<IColumnsSettingProps> = (props) => {
  const {
    checkedBaseColumns,
    checkedExtraColumns,
    extraColumns,
    customerLableColumns,
    checkedCustomerLableColumns,
    checkedAiColumns = [],
    onChange,
    aiExtractConfig,
    onAiExtractSave,
    onCloseAiExtract,
    onStartAiExtraction,
    onToggleAiFilter,
    handleSensorClickItem,
    isOpenNeteaseShelfReview
  } = props

  // 从 Redux store 获取数据
  const myRole = useAppSelector((state) => state.group.myRole)
  const groupId = useAppSelector((state) => state.group.groupId)
  const group = useAppSelector((state) => state.group)

  const [open, setOpen] = useState(false)
  const [columns, setColumns] = useState<string[]>([])
  const [showAiGuide, setShowAiGuide] = useState(false)
  const [hasShownGuide, setHasShownGuide] = useState(false)
  const [aiExtractModalVisible, setAiExtractModalVisible] = useState(false)

  // 获取AI相关列
  const getAiColumns = useCallback(() => {
    if (
      !aiExtractConfig ||
      !aiExtractConfig.isEnabled ||
      !aiExtractConfig.selectedScenarios.length
    ) {
      return []
    }

    const aiColumns: string[] = []
    aiExtractConfig.selectedScenarios.forEach((scenarioId) => {
      const scenario = AI_EXTRACT_SCENARIOS[scenarioId]
      if (scenario && scenario.columns) {
        scenario.columns.forEach((column) => {
          aiColumns.push(column.dataIndex)
        })
      }
    })
    return aiColumns
  }, [aiExtractConfig])

  useEffect(() => {
    setColumns([
      ...checkedAiColumns, // AI列在最前面
      ...checkedBaseColumns,
      ...checkedExtraColumns,
      ...checkedCustomerLableColumns
    ])
  }, [
    checkedBaseColumns,
    checkedExtraColumns,
    checkedCustomerLableColumns,
    checkedAiColumns
  ])

  const handleSensorClickModal = (clickItem: string, params: any = {}) => {
    const trackParams = {
      clickItem,
      ...params,
      role: sensorsRole(myRole),
      vipLevel: sensorsVipLevel(group),
      groupID: groupId,
      track_sign: 'aother.b4366.c7687.click.other_b4366_c7687_click',
      requirement_id: 387
    }
    sensors.track('other_b4366_c7687_click', trackParams)
  }

  const handleCancel = () => {
    setOpen(false)
    setColumns([
      ...checkedAiColumns, // AI列在最前面
      ...checkedBaseColumns,
      ...checkedExtraColumns,
      ...checkedCustomerLableColumns
    ])
  }
  const handleOK = useCallback(() => {
    sensors.track('web_watermarkdata_header_setting', { usedHeader: columns })
    setOpen(false)
    const baseColumnsDataIndex = baseColumnsData.map((data) => data.dataIndex)
    const checkedBaseColumns = columns.filter((column) =>
      baseColumnsDataIndex.includes(column)
    )
    const checkedExtraColumns = columns.filter((column) =>
      extraColumns.includes(column)
    )

    const checkedCustomerLableColumns = columns.filter((column) =>
      customerLableColumns.includes(column)
    )

    // 分离出AI列
    const aiColumns = getAiColumns()
    const checkedAiColumns = columns.filter((column) =>
      aiColumns.includes(column)
    )

    onChange(
      checkedBaseColumns,
      checkedExtraColumns,
      checkedCustomerLableColumns,
      checkedAiColumns
    )
  }, [columns, extraColumns, getAiColumns])

  // AI引导图片显示处理
  const handleAiButtonHover = useCallback(() => {
    if (!hasShownGuide) {
      setShowAiGuide(true)
      setHasShownGuide(true)
    }
  }, [hasShownGuide])

  // AI提取弹窗处理
  const handleAiExtractClick = useCallback(() => {
    handleSensorClickItem('AI提取')
    setShowAiGuide(false) // 点击时立即隐藏引导图
    setAiExtractModalVisible(true)
  }, [handleSensorClickItem])

  // AI引导图片隐藏处理
  const handleAiButtonMouseLeave = useCallback(() => {
    setShowAiGuide(false)
  }, [])

  // 处理AI引导图片的定时器
  useEffect(() => {
    if (showAiGuide) {
      const timer = setTimeout(() => {
        setShowAiGuide(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [showAiGuide])

  const handleAiExtractCancel = useCallback(() => {
    setAiExtractModalVisible(false)
  }, [])

  const handleAiExtractSave = useCallback(
    (selectedOptions: string[]) => {
      handleSensorClickItem('提取加载')
      if (onAiExtractSave) {
        onAiExtractSave(selectedOptions)
      }
      setAiExtractModalVisible(false)
    },
    [onAiExtractSave]
  )

  // 开始AI提取
  const handleStartAiExtraction = useCallback(() => {
    handleSensorClickItem('重新提取')
    if (onStartAiExtraction) {
      onStartAiExtraction(true) // 传入 true 表示强制刷新，跳过缓存
    }
  }, [onStartAiExtraction])

  // 切换AI筛选
  const handleToggleAiFilter = useCallback(
    (e: any) => {
      handleSensorClickItem('提取标签', {
        string1: e.target.checked ? '勾选' : '取消'
      })
      const checked = e.target.checked
      if (onToggleAiFilter) {
        onToggleAiFilter(checked)
      }
    },
    [onToggleAiFilter]
  )

  // 获取AI筛选文本
  const getAiFilterText = useCallback(() => {
    if (!aiExtractConfig?.selectedScenarios?.length) return ''

    const scenarioNames = aiExtractConfig.selectedScenarios.map(
      (scenarioId) => {
        const scenario = AI_EXTRACT_SCENARIOS[scenarioId]
        return scenario ? scenario.title : scenarioId
      }
    )

    return scenarioNames.join('、')
  }, [aiExtractConfig?.selectedScenarios])

  return (
    <div className="columns-setting">
      <div className="ai-extract-section">
        <Button
          className="ai-extract-btn"
          onMouseEnter={handleAiButtonHover}
          onMouseLeave={handleAiButtonMouseLeave}
          onClick={handleAiExtractClick}
        >
          <img src={aiIcon} alt="AI" className="ai-icon" />
          AI提取
        </Button>
        {aiExtractConfig?.isEnabled && (
          <Button
            className="retry-extract-btn"
            onClick={handleStartAiExtraction}
            disabled={aiExtractConfig?.isExtracting}
          >
            点击重新提取
          </Button>
        )}
        {/* AI筛选选项 */}
        {aiExtractConfig?.isEnabled &&
          aiExtractConfig?.selectedScenarios?.length > 0 && (
            <div className="ai-filter-section">
              <label className="ai-filter-checkbox">
                <input
                  type="checkbox"
                  checked={aiExtractConfig?.isFilterEnabled === true}
                  onChange={handleToggleAiFilter}
                />
                <span>仅展示{getAiFilterText()}照片</span>
              </label>
            </div>
          )}
        {showAiGuide && (
          <div className="ai-guide-overlay">
            <img src={aiGuideImage} alt="AI引导" className="ai-guide-image" />
          </div>
        )}
      </div>

      {/* AI提取数据弹窗 */}
      <AiExtractModal
        visible={aiExtractModalVisible}
        onCancel={handleAiExtractCancel}
        onSave={handleAiExtractSave}
        onCloseExtract={onCloseAiExtract}
        initialSelectedOptions={aiExtractConfig?.selectedScenarios || []}
        handleSensorClickModal={handleSensorClickModal}
      />

      <div className="action-section">
        <div className="button-list">
          <span
            key="0"
            style={{
              marginRight: '20px',
              cursor: 'pointer'
            }}
            onClick={() => {
              props.handleSensorClickItem('downloadRecord')
              window.open('/attendance/export-list')
            }}
          >
            下载记录
          </span>
          <Button
            icon={<UnorderedListOutlined />}
            type="primary"
            ghost
            onClick={() => {
              setOpen(true)
              props.handleSensorClickItem('listSetting')
            }}
          >
            列显示设置
          </Button>
        </div>
        <Select
          dropdownStyle={{ width: '160px', zIndex: 980 }} //viewer zIndex: 990
          open={open}
          value={columns}
          onChange={(value) => setColumns(value)}
          popupRender={(menuNode) => {
            return (
              <div>
                <h4
                  style={{
                    margin: 0,
                    borderBottom: 'solid 1px #EBEDF0',
                    textAlign: 'center',
                    lineHeight: '32px'
                  }}
                >
                  列显示设置
                </h4>
                {menuNode}
                <div
                  style={{
                    borderTop: 'solid 1px #EBEDF0',
                    textAlign: 'center'
                  }}
                >
                  <Button type="link" onClick={handleCancel}>
                    取消
                  </Button>
                  <Button type="link" onClick={handleOK}>
                    确定
                  </Button>
                </div>
              </div>
            )
          }}
          mode="multiple"
        >
          {/* AI提取相关列 */}
          {aiExtractConfig &&
            aiExtractConfig.isEnabled &&
            aiExtractConfig.selectedScenarios.map((scenarioId) => {
              const scenario = AI_EXTRACT_SCENARIOS[scenarioId]
              if (!scenario || !scenario.columns) return null

              return scenario.columns.map((column) => (
                <Option key={column.dataIndex} value={column.dataIndex}>
                  {column.title}
                </Option>
              ))
            })}

          {baseColumnsData
            .filter((item) => {
              // 排除客户相关列
              if (
                [
                  'extension.visitExplicitInfo.customerName',
                  'extension.visitExplicitInfo.customerAddress',
                  'extension.visitExplicitInfo.customerContractName',
                  'extension.visitExplicitInfo.customerTel'
                ].includes(item.dataIndex)
              ) {
                return false
              }

              // 如果未开启审核功能，排除审核相关列
              if (
                !props.isOpenNeteaseShelfReview &&
                (item.dataIndex === 'reviewResult' ||
                  item.dataIndex === 'reviewOperator')
              ) {
                return false
              }

              return true
            })
            .map((column, index) => (
              <Option key={column.dataIndex} value={column.dataIndex}>
                {column.title}
              </Option>
            ))}
          {props.extraColumns.map((column) => (
            <Option key={column} value={column}>
              {column}
            </Option>
          ))}

          {/*props.customerLableColumns.map((column, index) => (
          <Option key={index} value={column}>
            {column}
          </Option>
        )) */}

          {/* <Option
          key="extension.visitExplicitInfo.photographer"
          value="extension.visitExplicitInfo.photographer"
        >
          拍照人
        </Option> */}
        </Select>
      </div>
    </div>
  )
}

export default ColumnsSetting
